import React, { useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Collapse,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CiSearch } from "react-icons/ci";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PercentIcon from "@mui/icons-material/Percent";
import PeopleAltIcon from "@mui/icons-material/PeopleAltOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEventsOutlined";
import { Link, useNavigate } from "react-router-dom";
import { useGetSessionDataListQuery, useGetSessionDataKpisQuery } from "../../../Api/attoDeskApi";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { presentDateOnly } from "../../../utils/dateOnly";
import TodaysAbaSessions from "../shared/TodaysAbaSessions";
import { LegacyTokens, TypeScale, PageSurface, B13, B13Ink, tint12 } from "../../../theme/b13Tokens";
import { AbaUpdatedCell } from "../../ProgramLibrary/shared/abaTable";
import { AbaSessionStatusChip, PromptLevelChip, MeasurementChip } from "../shared/SessionBadges";
import { toMeasurementTypeEnum } from "../shared/measurementType";
import { measurementTypeLabel } from "../shared/measurementTypeOptions";

/**
 * Session Data list / dashboard.
 *
 * The sidebar's Session Data item lands here (not the recorder directly).
 * Batch 39 — visual redesign only: header, KPI cards, tabs, search,
 * table and pagination all migrated onto the shared abaTokens set.
 * Every RTK-Query call, filter variable, tab key, pagination handler and
 * action route from the prior implementation is preserved verbatim.
 */
const TABS = [
  { key: "all",      label: "All Sessions" },
  { key: "today",    label: "Today" },
  { key: "drafts",   label: "Drafts" },
  { key: "review",   label: "Awaiting Review" },
  { key: "reviewed", label: "Reviewed" },
  { key: "complete", label: "Completed" },
  { key: "returned", label: "Returned" },
  { key: "void",     label: "Voided" },
];

type Row = {
  id: number;
  sessionDate: string | null;
  studentName?: string | null;
  staffName?: string | null;
  programName?: string | null;
  targetName?: string | null;
  trialsCompleted: number;
  correctTrials: number;
  percentCorrect: number;
  promptLevelUsed?: string | null;
  // Batch 13 Part C Step 4a — attached by the list endpoint via the
  // modal promptLevelId; live colour from prompt_levels so a recolour
  // propagates. Null when the level has been deleted since recording
  // or when the session has no prompt entries. Name (promptLevelUsed)
  // continues to come from the session-record snapshot.
  promptLevelColour?: string | null;
  status?: string | null;
  measurementType?: string | null;
  durationMinutes?: number | null;
  reviewedBy?: number | null;
  updated_at?: string | null;
};

export default function SessionDataList() {
  const navigate = useNavigate();
  // Batch 44 — three-tier responsive: full desktop (≥1440), compact
  // (1024–1439, 7 cols + expandable details), mobile cards (<1024).
  // The current MUI theme has md=900 lg=1200 xl=1536 which don't match
  // the clinical spec, so we use raw pixel queries.
  const isLargeDesktop = useMediaQuery("(min-width:1440px)");
  const isCompact = useMediaQuery("(min-width:1024px) and (max-width:1439.98px)");
  const isMobile = useMediaQuery("(max-width:1023.98px)");
  const [expandedRows, setExpandedRows] = useState<Set<number | string>>(new Set());
  const toggleExpanded = (id: number | string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Batch 36B — surface KPI loading so the KPI cards below hide the
  // pre-response all-zero state instead of flashing to real values.
  const { data: kpiData, isFetching: kpiLoading } = useGetSessionDataKpisQuery({});
  const { data, isLoading } = useGetSessionDataListQuery({
    tab,
    searchText: debouncedSearch,
    page,
    perPage,
  });

  const rows: Row[] = useMemo(
    () => (Array.isArray(data?.data) ? (data!.data as Row[]) : []),
    [data]
  );
  const meta = (data as any)?.meta as { total: number; lastPage: number } | undefined;
  const total = meta?.total ?? 0;
  const lastPage = meta?.lastPage ?? 1;
  const kpis = (kpiData?.data as any) || {};

  React.useEffect(() => setPage(1), [tab, debouncedSearch, perPage]);

  // Batch 39 — six premium KPI cards, same six metrics from the same
  // useGetSessionDataKpisQuery call. Suppressed entirely while loading and
  // when every figure is zero, per the Batch 36B discipline.
  type KpiDef = { key: string; label: string; icon: React.ReactNode; accent: string; tile: string; suffix?: string };
  const kpiDefs: KpiDef[] = [
    { key: "sessionsToday",   label: "Sessions Today",   icon: <EventNoteIcon fontSize="small" />,        accent: LegacyTokens.teal,    tile: LegacyTokens.tealSoft },
    { key: "trialsToday",     label: "Trials Today",     icon: <PercentIcon fontSize="small" />,          accent: LegacyTokens.blue,    tile: LegacyTokens.blueSoft },
    // Batch 58 §C2 — the KPI labels must state their time scope. The
    // backend computes averageAccuracy over rows with sessionDate >=
    // startOfWeek(Monday), and studentsSeen as distinct studentId
    // with sessionDate = today. Bare "Average Accuracy" / "Students
    // Seen" read as all-time; the demo showed a 0% and a "1" that
    // both invited misreading. Time scope belongs in the label.
    { key: "averageAccuracy", label: "Avg Accuracy This Week", icon: <CheckCircleIcon fontSize="small" />,      accent: LegacyTokens.emerald, tile: LegacyTokens.emeraldSoft, suffix: "%" },
    { key: "studentsSeen",    label: "Students Seen Today",    icon: <PeopleAltIcon fontSize="small" />,        accent: LegacyTokens.purple,  tile: LegacyTokens.purpleSoft },
    { key: "targetsMastered", label: "Targets Mastered", icon: <EmojiEventsIcon fontSize="small" />,      accent: B13.primary,   tile: tint12(B13.primary) },
    { key: "awaitingReview",  label: "Awaiting Review",  icon: <HourglassEmptyIcon fontSize="small" />,   accent: LegacyTokens.amber,   tile: LegacyTokens.amberSoft },
  ];
  const kpisAllZero = kpiDefs.every((d) => !kpis[d.key]);
  const showKpis = !kpiLoading && !kpisAllZero;

  // Batch 44 §15 — reduced desktop padding from 3.5 (28px) to 3 (24px)
  // on each side. Combined with the fixed 11-col table (~1445px), this
  // fits inside a 1440px viewport once the shell's fixed sidebar
  // (~280px) is subtracted from a 1920px screen (~1640px available).
  // Padding stays at 2 (16px) on small screens where the mobile card
  // layout kicks in anyway.
  return (
    <Box sx={{ ...PageSurface, p: { xs: 2, md: 3 } }}>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 1.5, fontSize: 12 }} separator="/">
          <MuiLink component={Link} to="/aba-therapy/programs" underline="hover" sx={{ color: B13Ink.ink3 }}>
            ABA Therapy
          </MuiLink>
          <Typography sx={{ fontSize: 12, color: LegacyTokens.ink }}>Session Data</Typography>
        </Breadcrumbs>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: { md: "flex-start" }, justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", minWidth: 0 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
              backgroundColor: LegacyTokens.tealSoft, color: LegacyTokens.teal,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <EventNoteIcon fontSize="medium" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography component="h1" sx={{ ...TypeScale.pageTitle, lineHeight: 1.15 }}>
                  ABA Session Data
                </Typography>
                <Box sx={{
                  display: "inline-flex", alignItems: "center",
                  backgroundColor: LegacyTokens.tealSoft, color: LegacyTokens.teal,
                  fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  px: 1, py: 0.25, borderRadius: `${LegacyTokens.radius.chip}px`,
                }}>ABA Clinical Sessions</Box>
              </Box>
              <Typography sx={{ ...TypeScale.caption, mt: 0.5 }}>
                Record Trials, review clinical Session data, and monitor learner progress.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained" size="medium"
            startIcon={<PlayArrowIcon />}
            onClick={() => navigate("/aba-therapy/sessions/new")}
            sx={{
              backgroundColor: B13.primary, color: LegacyTokens.surface,
              fontWeight: 600, textTransform: "none",
              px: 2, py: 1, borderRadius: `${LegacyTokens.radius.control}px`,
              boxShadow: "0 1px 2px rgba(15, 61, 46, 0.15)",
              flexShrink: 0,
              "&:hover": { backgroundColor: B13.primary, boxShadow: "0 2px 6px rgba(15, 61, 46, 0.25)" },
            }}
          >
            Record New Session
          </Button>
        </Box>
      </Box>

      <TodaysAbaSessions />

      {/* ── KPI cards ──────────────────────────────────────────────── */}
      {showKpis && (
        <Grid container spacing={2} sx={{ mb: 3 }} data-testid="session-stat-strip">
          {kpiDefs.map((k) => (
            <Grid key={k.key} item xs={6} sm={4} md={2}>
              <Box sx={{
                height: "100%", p: 2,
                display: "flex", flexDirection: "column", gap: 1.25,
                backgroundColor: LegacyTokens.surface,
                border: `1px solid ${LegacyTokens.line}`,
                borderRadius: `${LegacyTokens.radius.card}px`,
                transition: "box-shadow 150ms",
                "&:hover": { boxShadow: "0 2px 8px rgba(15, 61, 46, 0.06)" },
              }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: `${LegacyTokens.radius.control}px`,
                  backgroundColor: k.tile, color: k.accent,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{k.icon}</Box>
                <Box>
                  <Typography sx={{ fontSize: 24, fontWeight: 650, color: LegacyTokens.ink, lineHeight: 1.1 }}>
                    {kpis[k.key] ?? 0}{k.suffix ?? ""}
                  </Typography>
                  <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mt: 0.5 }}>
                    {k.label}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Tabs + search ──────────────────────────────────────────── */}
      <Card variant="outlined" sx={{
        borderRadius: `${LegacyTokens.radius.card}px`,
        borderColor: LegacyTokens.line,
        boxShadow: "none",
        overflow: "hidden",
        mb: 2,
      }}>
        <Box sx={{ borderBottom: `1px solid ${LegacyTokens.line}`, px: 2 }}>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none", fontSize: 14, fontWeight: 500,
                color: B13Ink.ink3, minHeight: 48, px: 2, py: 1.5,
                "&:hover": { color: B13Ink.ink2, backgroundColor: "transparent" },
              },
              "& .Mui-selected": { color: `${LegacyTokens.ink} !important`, fontWeight: 600 },
              "& .MuiTabs-indicator": { backgroundColor: B13.primary, height: 2 },
            }}>
            {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
          </Tabs>
        </Box>
        <Box sx={{ p: 2 }}>
          <TextField
            size="small" fullWidth
            placeholder="Search by Student, Program, or Target"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              maxWidth: 520,
              "& .MuiOutlinedInput-root": {
                backgroundColor: B13.canvas, height: 44,
                borderRadius: `${LegacyTokens.radius.control}px`,
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: LegacyTokens.line },
                "&.Mui-focused fieldset": { borderColor: B13.primary },
              },
            }}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><CiSearch style={{ fontSize: 18, color: B13Ink.ink3 }} /></InputAdornment>),
            }}
          />
        </Box>
      </Card>

      {/* ── Table ──────────────────────────────────────────────────── */}
      {/* Batch 44 — three-tier responsive layout. Desktop table combines
         Program+Target and Prompt+Duration into two-line cells so all 11
         columns fit at 1440+ without horizontal scroll. Compact
         (1024-1439) drops Staff/Measurement/Trials/Prompt/Duration into
         an expandable row-details area. Mobile (<1024) renders each
         session as a stacked card. Pagination lives OUTSIDE any
         horizontal scroller so it never shifts with table content. */}
      <Card variant="outlined" sx={{
        borderRadius: `${LegacyTokens.radius.card}px`,
        borderColor: LegacyTokens.line,
        boxShadow: "none",
        overflow: "hidden",
      }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={28} sx={{ color: B13.primary }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Box sx={{
              display: "inline-flex", width: 48, height: 48, borderRadius: `${LegacyTokens.radius.control}px`,
              backgroundColor: LegacyTokens.tealSoft, color: LegacyTokens.teal,
              alignItems: "center", justifyContent: "center", mb: 2,
            }}>
              <EventNoteIcon />
            </Box>
            <Typography sx={{ ...TypeScale.sectionHead, mb: 0.5 }}>
              {debouncedSearch ? "No Sessions found" : "No Session data yet"}
            </Typography>
            <Typography sx={{ ...TypeScale.caption, maxWidth: 380, mx: "auto", mb: 2.5 }}>
              {debouncedSearch
                ? "Try adjusting your search or record a new ABA Session."
                : "Session records will appear here as staff record ABA Trials."}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              {debouncedSearch && (
                <Button size="small" onClick={() => setSearch("")}
                  sx={{
                    color: B13.primary, textTransform: "none", fontWeight: 600,
                    border: `1px solid ${tint12(B13.primary)}`,
                    "&:hover": { backgroundColor: tint12(B13.primary) },
                  }}>
                  Clear Search
                </Button>
              )}
              <Button variant="contained" size="small" startIcon={<PlayArrowIcon />}
                onClick={() => navigate("/aba-therapy/sessions/new")}
                sx={{
                  backgroundColor: B13.primary, color: LegacyTokens.surface,
                  fontWeight: 600, textTransform: "none",
                  "&:hover": { backgroundColor: B13.primary },
                }}>
                Record New Session
              </Button>
            </Stack>
          </Box>
        ) : isMobile ? (
          // ── Mobile: session cards ─────────────────────────────────
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${LegacyTokens.line}` }} />}>
            {rows.map((r) => <MobileSessionCard key={r.id} row={r} onNavigate={navigate} />)}
          </Stack>
        ) : (
          // ── Desktop + Compact: shared Table with variant switch ───
          <Box sx={{ overflowX: "hidden" }} data-testid="session-table-container">
            <Table size="small" sx={{
              width: "100%",
              tableLayout: "fixed",
              "& td, & th": { borderBottomColor: LegacyTokens.line, py: 1.5, px: 2 },
            }}>
              {isLargeDesktop ? (
                <>
                  {/* §9 — desktop widths totalling ~1445px, fitting inside a 1440
                     viewport once the app shell padding is accounted for. */}
                  <colgroup>
                    <col style={{ width: 88 }} />
                    <col style={{ width: 145 }} />
                    <col style={{ width: 140 }} />
                    <col style={{ minWidth: 240 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 70 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 135 }} />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 112 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: B13.canvas, "& th": { height: 48 } }}>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Date</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Student</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Staff</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Program / Target</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Measurement</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }} align="right">Trials</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }} align="right">Primary</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Prompt / Duration</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Status</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Updated</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                </>
              ) : (
                <>
                  {/* Compact 7-col: 88+145+240+110+130+120+112 = 945px + 40px chevron */}
                  <colgroup>
                    <col style={{ width: 40 }} />
                    <col style={{ width: 88 }} />
                    <col style={{ width: 145 }} />
                    <col style={{ minWidth: 240 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 112 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: B13.canvas, "& th": { height: 48 } }}>
                      <TableCell sx={{ ...TypeScale.tableHeader, px: 1 }} />
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Date</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Student</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Program / Target</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }} align="right">Primary</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Status</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }}>Updated</TableCell>
                      <TableCell sx={{ ...TypeScale.tableHeader }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                </>
              )}
              <TableBody>
                {rows.map((r) => {
                  // Route through the resolver so both storage shapes
                  // (legacy varchar "Percent Correct" and enum
                  // "trial_percent_correct") render as "Trial Accuracy".
                  // Was a one-case ternary that only matched the varchar.
                  const rEnum = toMeasurementTypeEnum(r.measurementType);
                  const isPercentMetric = !r.measurementType || rEnum === "trial_percent_correct";
                  const measurementLabel = rEnum ? measurementTypeLabel(rEnum) : (r.measurementType || null);
                  const primaryMetric = !isPercentMetric
                    ? "—"
                    : (r.trialsCompleted ?? 0) > 0
                      ? `${Number(r.percentCorrect).toFixed(0)}%`
                      : "—";
                  const durationText = r.durationMinutes ? `${r.durationMinutes}m` : null;
                  const isExpanded = expandedRows.has(r.id);
                  if (isLargeDesktop) {
                    return (
                      <TableRow key={r.id} hover sx={{ height: 68 }}>
                        <TableCell sx={{ ...TypeScale.tableCell, color: LegacyTokens.ink, whiteSpace: "nowrap" }}>
                          {presentDateOnly(r.sessionDate)}
                        </TableCell>
                        <TableCell sx={{ ...TypeScale.tableCell, color: LegacyTokens.ink, fontWeight: 600 }}>
                          <TwoLineClamp title={r.studentName}>{r.studentName || <Dash />}</TwoLineClamp>
                        </TableCell>
                        <TableCell sx={{ ...TypeScale.tableCell }}>
                          <TwoLineClamp title={r.staffName}>{r.staffName || <Dash />}</TwoLineClamp>
                        </TableCell>
                        <TableCell sx={{ ...TypeScale.tableCell }}>
                          <ProgramTargetCell programName={r.programName} targetName={r.targetName} />
                        </TableCell>
                        <TableCell>
                          {measurementLabel ? <MeasurementChip label={measurementLabel} /> : <Dash />}
                        </TableCell>
                        <TableCell align="right" sx={{ ...TypeScale.tableCell, color: LegacyTokens.ink }}>
                          {r.trialsCompleted ?? 0}
                        </TableCell>
                        <TableCell align="right" sx={{ ...TypeScale.tableCell, fontWeight: 600, color: LegacyTokens.ink }}>
                          {primaryMetric}
                        </TableCell>
                        <TableCell>
                          <PromptDurationCell prompt={r.promptLevelUsed} promptColour={r.promptLevelColour} duration={durationText} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <AbaSessionStatusChip status={r.status} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <AbaUpdatedCell value={r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"} />
                        </TableCell>
                        <TableCell align="center" sx={{ px: 1.5 }}>
                          <SessionRowActions row={r} onNavigate={navigate} />
                        </TableCell>
                      </TableRow>
                    );
                  }
                  // Compact row + expandable details
                  return (
                    <React.Fragment key={r.id}>
                      <TableRow hover sx={{ height: 64, "& > *": { borderBottom: isExpanded ? "none" : undefined } }}>
                        <TableCell sx={{ px: 1 }}>
                          <IconButton size="small" onClick={() => toggleExpanded(r.id)}
                            aria-label={isExpanded ? "Hide session details" : "Show session details"}
                            sx={{ color: B13Ink.ink3 }}>
                            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ ...TypeScale.tableCell, color: LegacyTokens.ink, whiteSpace: "nowrap" }}>
                          {presentDateOnly(r.sessionDate)}
                        </TableCell>
                        <TableCell sx={{ ...TypeScale.tableCell, color: LegacyTokens.ink, fontWeight: 600 }}>
                          <TwoLineClamp title={r.studentName}>{r.studentName || <Dash />}</TwoLineClamp>
                        </TableCell>
                        <TableCell sx={{ ...TypeScale.tableCell }}>
                          <ProgramTargetCell programName={r.programName} targetName={r.targetName} />
                        </TableCell>
                        <TableCell align="right" sx={{ ...TypeScale.tableCell, fontWeight: 600, color: LegacyTokens.ink }}>
                          {primaryMetric}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <AbaSessionStatusChip status={r.status} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <AbaUpdatedCell value={r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"} />
                        </TableCell>
                        <TableCell align="center" sx={{ px: 1.5 }}>
                          <SessionRowActions row={r} onNavigate={navigate} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0, borderBottom: `1px solid ${LegacyTokens.line}` }}>
                          <Collapse in={isExpanded} unmountOnExit>
                            <SessionRowDetails
                              row={r}
                              measurementLabel={measurementLabel}
                              durationText={durationText}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Pagination footer */}
        <Box sx={{
          borderTop: `1px solid ${LegacyTokens.line}`,
          backgroundColor: B13.canvas,
          p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 1,
        }}>
          <Typography sx={{ ...TypeScale.caption }}>
            {total === 0
              ? "0 Sessions"
              : (() => {
                  const from = Math.min(total, (page - 1) * perPage + 1);
                  const to = Math.min(total, page * perPage);
                  return `Showing ${from}–${to} of ${total} Session${total === 1 ? "" : "s"}`;
                })()
            }
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ ...TypeScale.caption }}>Rows</Typography>
            <FormControl size="small">
              <Select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}
                sx={{
                  minWidth: 80, height: 36,
                  backgroundColor: LegacyTokens.surface,
                  borderRadius: `${LegacyTokens.radius.control}px`,
                  "& fieldset": { borderColor: LegacyTokens.line },
                }}>
                {[25, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="small" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{
                color: B13.primary, textTransform: "none", fontWeight: 600,
                border: `1px solid ${tint12(B13.primary)}`,
                backgroundColor: LegacyTokens.surface,
                "&:hover": { backgroundColor: tint12(B13.primary) },
                "&.Mui-disabled": { color: B13Ink.ink3, borderColor: LegacyTokens.line },
              }}>Prev</Button>
            <Button size="small" disabled={page >= lastPage}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              sx={{
                color: B13.primary, textTransform: "none", fontWeight: 600,
                border: `1px solid ${tint12(B13.primary)}`,
                backgroundColor: LegacyTokens.surface,
                "&:hover": { backgroundColor: tint12(B13.primary) },
                "&.Mui-disabled": { color: B13Ink.ink3, borderColor: LegacyTokens.line },
              }}>Next</Button>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
}

// Batch 39 — em-dash placeholder in ink3 so "no data" reads as such.
const Dash: React.FC = () => <Box component="span" sx={{ color: B13Ink.ink3 }}>—</Box>;

// Batch 44 — 2-line clamp with tooltip for cells that may hold long
// values (student, staff, program, target). Wraps text at word breaks
// and shows the full value on hover when clamped.
const TwoLineClamp: React.FC<{ title?: string | null; children: React.ReactNode }> = ({ title, children }) => (
  <Tooltip title={title || ""} disableHoverListener={!title || title.length < 30} arrow>
    <Box sx={{
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      overflowWrap: "anywhere",
      lineHeight: 1.35,
    }}>
      {children}
    </Box>
  </Tooltip>
);

// Batch 44 §2 — merged Program / Target cell. Program in medium-weight
// primary ink, Target beneath in secondary ink2 with a small "Target"
// caption prefix. Two lines each maximum; full value on hover.
const ProgramTargetCell: React.FC<{ programName?: string | null; targetName?: string | null }> = ({ programName, targetName }) => (
  <Box sx={{ minWidth: 0 }}>
    <Tooltip title={programName || ""} disableHoverListener={!programName || (programName ?? "").length < 40} arrow>
      <Typography sx={{
        fontSize: 13.5, fontWeight: 600, color: LegacyTokens.ink, lineHeight: 1.3,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        overflow: "hidden", overflowWrap: "anywhere",
      }}>
        {programName || <Dash />}
      </Typography>
    </Tooltip>
    {targetName && (
      <Tooltip title={targetName || ""} disableHoverListener={targetName.length < 40} arrow>
        <Typography sx={{
          fontSize: 12, color: B13Ink.ink3, lineHeight: 1.3, mt: 0.25,
          display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
          overflow: "hidden", overflowWrap: "anywhere",
        }}>
          {targetName}
        </Typography>
      </Tooltip>
    )}
  </Box>
);

// Batch 44 §2 — merged Prompt / Duration cell. Prompt chip on top,
// duration text below. If both empty, single dash; if only one, that
// value alone (no reserved slot for the missing half).
const PromptDurationCell: React.FC<{ prompt?: string | null; promptColour?: string | null; duration?: string | null }> = ({ prompt, promptColour, duration }) => {
  if (!prompt && !duration) return <Dash />;
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      {prompt ? <PromptLevelChip level={prompt} colour={promptColour} /> : null}
      {duration ? (
        <Typography sx={{ fontSize: 12, color: B13Ink.ink3, whiteSpace: "nowrap" }}>
          {duration}
        </Typography>
      ) : null}
    </Stack>
  );
};

// Batch 44 §8 — expandable details for the compact (1024-1439) table.
// Renders the fields dropped from the collapsed row: staff, measurement,
// trials, prompt, duration, and reviewer/phase if present.
const SessionRowDetails: React.FC<{
  row: Row;
  measurementLabel: string | null;
  durationText: string | null;
}> = ({ row, measurementLabel, durationText }) => (
  <Box sx={{
    px: 3, py: 2,
    backgroundColor: LegacyTokens.canvas,
    borderTop: `1px solid ${LegacyTokens.line}`,
  }}>
    <Grid container spacing={2}>
      <DetailField label="Staff" value={row.staffName} />
      <DetailField label="Measurement" value={measurementLabel} />
      <DetailField label="Trials" value={String(row.trialsCompleted ?? 0)} />
      <DetailField
        label="Prompt"
        value={row.promptLevelUsed ? <PromptLevelChip level={row.promptLevelUsed} colour={row.promptLevelColour} /> : null}
      />
      <DetailField label="Duration" value={durationText} />
      {row.reviewedBy != null && (
        <DetailField label="Reviewer" value={String(row.reviewedBy)} />
      )}
    </Grid>
  </Box>
);
const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={6} sm={4} md={2}>
    <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.5 }}>{label}</Typography>
    <Box sx={{ fontSize: 13, color: B13Ink.ink2 }}>
      {value ?? <Dash />}
    </Box>
  </Grid>
);

// Batch 44 §7 — mobile session card. Header shows date + status + row
// actions; body stacks student, staff, program, target, measurement,
// primary metric, prompt and duration in a two-column grid. No table
// on mobile; no horizontal scroll.
const MobileSessionCard: React.FC<{ row: Row; onNavigate: (p: string) => void }> = ({ row, onNavigate }) => {
  // Same resolver as the desktop rows above — both storage shapes
  // resolve to "Trial Accuracy". Was a one-case ternary too.
  const rEnum = toMeasurementTypeEnum(row.measurementType);
  const isPercentMetric = !row.measurementType || rEnum === "trial_percent_correct";
  const measurementLabel = rEnum ? measurementTypeLabel(rEnum) : (row.measurementType || null);
  const primaryMetric = !isPercentMetric
    ? "—"
    : (row.trialsCompleted ?? 0) > 0
      ? `${Number(row.percentCorrect).toFixed(0)}%`
      : "—";
  const durationText = row.durationMinutes ? `${row.durationMinutes}m` : null;
  return (
    <Box sx={{ p: 2, backgroundColor: LegacyTokens.surface }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: LegacyTokens.ink }}>
            {presentDateOnly(row.sessionDate)}
          </Typography>
          <AbaSessionStatusChip status={row.status} />
        </Stack>
        <SessionRowActions row={row} onNavigate={onNavigate} />
      </Stack>
      <ProgramTargetCell programName={row.programName} targetName={row.targetName} />
      <Grid container spacing={1.5} sx={{ mt: 1 }}>
        <DetailField label="Student" value={row.studentName} />
        <DetailField label="Staff" value={row.staffName} />
        <DetailField label="Measurement" value={measurementLabel} />
        <DetailField label="Primary" value={<Box sx={{ fontWeight: 600, color: LegacyTokens.ink }}>{primaryMetric}</Box>} />
        <DetailField label="Trials" value={String(row.trialsCompleted ?? 0)} />
        <DetailField
          label="Prompt"
          value={row.promptLevelUsed ? <PromptLevelChip level={row.promptLevelUsed} colour={row.promptLevelColour} /> : null}
        />
        <DetailField label="Duration" value={durationText} />
      </Grid>
    </Box>
  );
};

/**
 * Status-specific Session row actions. Draft / Completed / Awaiting Review /
 * Reviewed / Returned for Correction / Voided each get their own action set.
 * Backend transition validation stays authoritative. Batch 39 upgraded the
 * icon-button size + tooltip UX; every route and handler is unchanged.
 */
function SessionRowActions({ row, onNavigate }: { row: Row; onNavigate: (path: string) => void }) {
  const status = (row.status || "").toLowerCase();
  const go = (path: string) => onNavigate(path);
  const btn = {
    width: 36, height: 36, borderRadius: "50%",
    color: B13Ink.ink3,
    "&:hover": { backgroundColor: B13.canvas, color: B13Ink.ink2 },
  };
  // Batch 46 — inline-flex so a parent TableCell with align="center"
  // actually centres this block, but the mobile card's
  // justify-content:space-between header still places it on the right
  // edge as a shrink-wrapped flex child.
  return (
    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ display: "inline-flex" }} data-testid={`session-actions-${row.id}`}>
      <Tooltip title="View Session" arrow>
        <IconButton onClick={() => go(`/aba-therapy/sessions/${row.id}`)} sx={btn}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {status === "draft" && (
        <Tooltip title="Continue Draft" arrow>
          <IconButton onClick={() => go(`/aba-therapy/sessions/${row.id}/edit`)}
            sx={{ ...btn, color: B13.primary, "&:hover": { backgroundColor: tint12(B13.primary), color: B13.primary } }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {status === "completed" && (
        <Tooltip title="Submit for Review" arrow>
          <IconButton onClick={() => go(`/aba-therapy/sessions/${row.id}/review`)}
            sx={{ ...btn, color: B13.primary, "&:hover": { backgroundColor: tint12(B13.primary), color: B13.primary } }}>
            <PlayArrowIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {status === "awaiting_review" && (
        <Tooltip title="Review" arrow>
          <IconButton onClick={() => go(`/aba-therapy/sessions/${row.id}/review`)}
            sx={{ ...btn, color: B13.primary, "&:hover": { backgroundColor: tint12(B13.primary), color: B13.primary } }}>
            <PlayArrowIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {status === "returned_for_correction" && (
        <Tooltip title="Correct Session" arrow>
          <IconButton onClick={() => go(`/aba-therapy/sessions/${row.id}/edit`)}
            sx={{ ...btn, color: LegacyTokens.amber, "&:hover": { backgroundColor: LegacyTokens.amberSoft, color: LegacyTokens.amber } }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {status === "voided" && (
        <Tooltip title="Restore" arrow>
          <IconButton onClick={() => go(`/aba-therapy/sessions/${row.id}/review`)} sx={btn}>
            <PlayArrowIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
